import { stringValueOrEmpty, styleEm } from "@tevari/helpers";
import Image from "next/image";
import { FunctionComponent, memo, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import styles from "./CriteriumItem.module.scss";

import Modal from "@components/generic/modal/modal";
import Symbol from "@components/generic/symbol";
import SubcriteriumModal from "@components/referential/subcriterium/subcriteriumModal";
import { DataLayer, GTM } from "@lib/gtm";
import { commonImages } from "@lib/images";
import { IBrandSource } from "@lib/store/models/brandsModels";
import { ICriteriumNode } from "@lib/store/models/nodesModels";
import { useReferential } from "context/referentialContext";

export interface ICriteriumItemProps {
  sectionName: string;
  criterium: ICriteriumNode;
  brandSources: IBrandSource[];
}

const CriteriumItem: FunctionComponent<ICriteriumItemProps> = ({
  sectionName,
  criterium: { id, name, symbol, percentage, code },
  brandSources,
}) => {
  const [showModal, setShowModal] = useState(false);

  const { depth } = useReferential();

  const isLastDepth = depth === "criteriums";

  useEffect(() => {
    const trackingEvent: DataLayer = {
      event: "click_criterium_brand",
      criteriumName: code,
    };

    if (showModal) GTM.trackEvent(trackingEvent);
  }, [showModal, code]);

  return (
    <div className={styles["criterium-item"]}>
      <div
        className={`${styles["criterium-item__toggle-card"]} ${stringValueOrEmpty(isLastDepth && styles["disabled"])}`}
        onClick={() => {
          if (isLastDepth) return;

          setShowModal(true);
        }}
        id="cta-brand-criterium"
      >
        <div className={styles["criterium-item__content"]}>
          <h4 className={styles["criterium-item__title"]}>{name}</h4>
          {symbol && <Symbol symbol={symbol} percentage={percentage} className={styles["criterium-item__symbol"]} />}
        </div>
        {!isLastDepth && (
          <div className={styles["criterium-item__toggle"]}>
            <Image
              src={commonImages.chevron}
              alt={"chevron"}
              height={16}
              width={9}
              sizes="10vw"
              style={{
                width: "100%",
                height: "auto",
              }}
            />
          </div>
        )}
      </div>
      <Modal onClose={() => setShowModal(false)} show={showModal} title={sectionName}>
        <SubcriteriumModal
          criteriumName={name}
          criteriumId={id}
          symbol={symbol}
          percentage={percentage}
          brandSources={brandSources || []}
        />
      </Modal>
    </div>
  );
};

export const CriteriumItemSkeleton = () => {
  return (
    <div className={`${styles["criterium-item"]}`}>
      <div
        className={`${styles["criterium-item__toggle-card"]} ${styles["criterium-item__toggle-card--disabled"]} ${styles["criterium-item__toggle-card__skeleton"]}`}
      >
        <Skeleton width="100%" height={styleEm(16)} />
      </div>
    </div>
  );
};

export default memo(CriteriumItem);
